import React from 'react';
import { TableCell, TableSortLabel, Tooltip, Box } from '@mui/material';

/**
 * Header cell that renders a 3-state TableSortLabel driven by a useTableSort instance.
 * Shows a numbered indicator (1, 2, 3) next to the arrow when multiple sort columns are active,
 * and a tooltip on the active primary column hinting at shift-click for multi-column sort.
 *
 * Plain `<TableCell>` is rendered when `sort` is not provided or `columnKey` is missing.
 *
 * @param {object} props
 * @param {object} [props.sort] - the value returned by useTableSort()
 * @param {string} props.columnKey - the column identifier
 * @param {string} [props.align='left']
 * @param {React.ReactNode} props.children - header label
 */
const SortableHeaderCell = ({ sort, columnKey, align = 'left', children, ...rest }) => {
  if (!sort || !columnKey) {
    return <TableCell align={align} {...rest}>{children}</TableCell>;
  }

  const idx = sort.sorts.findIndex((s) => s.column === columnKey);
  const active = idx !== -1;
  const dir = active ? sort.sorts[idx].dir : 'asc';
  const showNumber = sort.sorts.length > 1 && active;
  const isPrimaryActive = active && idx === 0 && sort.sorts.length === 1;

  const label = (
    <TableSortLabel
      active={active}
      direction={dir}
      onClick={(e) => sort.handleSort(columnKey, e)}
    >
      {children}
      {showNumber && (
        <Box
          component="span"
          sx={{ ml: 0.5, fontSize: '0.7em', color: 'text.secondary', fontWeight: 600 }}
        >
          {idx + 1}
        </Box>
      )}
    </TableSortLabel>
  );

  return (
    <TableCell align={align} sortDirection={active ? dir : false} {...rest}>
      {isPrimaryActive ? (
        <Tooltip title="Shift-click another header to add a secondary sort" arrow>
          <span>{label}</span>
        </Tooltip>
      ) : label}
    </TableCell>
  );
};

export default SortableHeaderCell;
